import { VStack, useDisclosure, HStack, Skeleton, Box, useColorMode } from '@chakra-ui/react';
import { Typography } from 'Tokens';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
  AggregatedQualitativeAnswers,
  hasQuarterlyFrequency,
  isFullMetricQuarterlyLevel,
} from '../AggregatedMetrics/AggregatedMetrics.hooks';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'Atoms';
import { LearnMoreDrawer, Loader, MetricLearnMoreHeader } from 'Molecules';
import {
  GetAnswersForDrMetricsOnCompanyLevelDocument_,
  GetAnswersForDrMetricsOnGroupLevelDocument_,
  GetAnswersForMetricsOnCompanyLevelDocument_,
  GetRequiredMaterialMetricsQuery_,
  GetSubAnswersPerDrOnGroupLevelDocument_,
  GetSubMaterialMetricsPerDrOnGroupLevelDocument_,
  QuestionType_Enum_,
  useGetReportingUnitQuery,
  useGetAssessmentCurrencyConversionQuery,
  useOptOutMetricMutation,
  GetMetricAnswersDocument_,
  GetOptedOutMetricsDocument_,
  GetSingleEsrsMetricAnswerDocument_,
} from 'models';
import { TimePeriods, TimePeriodsEnums } from '../../Requirement';
import { useCompanyType, useCurrentCompany, useToast } from 'utils/hooks';
import {
  SelectedMetric,
  DREnums,
  MetricsTableData,
  useMaterialStandardId,
  FrameworkRefs,
  Frameworks,
  AggregatedMetricsTableData,
} from 'containers/Esrs';
import { OptOutModal } from '../OptOutModal';
import { useApolloClient } from '@apollo/client';
import { MetricSidebar } from '../MetricSidebar';
import { IconArrowNarrowDown } from '@tabler/icons-react';
import { MetricsAISection } from '../MetricAI';
import { MetricsExcelUploadModal } from '../MetricsExcel';
import { GroupOrderOptions } from '../AggregatedMetrics/AggregatedMetricsUtils';
import { isEqual, uniqBy } from 'lodash';
import { MetricsTable } from '../MetricsTable/MetricsTable';
import { AnswersApprovalWarningModal } from '../MetricAI/AIUtils';
import { MDRM } from '../MDRM/MDRM';
import { filterCollectedChildMetrics, useProcessMetrics } from './ProcessMetrics/ProcessMetrics';
import { MetricsHeader } from './MetricsComponents/MetricsHeader';
import { MetricConfigurationWarning } from './MetricsComponents/MetricConfigurationWarning';
import { MetricsQuarterInfo } from './MetricsComponents/MetricsQuarterInfo';
import { MetricTableHeader } from './MetricsComponents/MetricTableHeader';
import {
  GeneratedSummaryStatus,
  getMetricStatus,
  getRemainingRows,
  getTaskReferences,
  useHandleTaskUpdate,
} from '.';
import { useTranslation } from 'utils/translation';
import { TaskToolbar } from 'Molecules/TaskToolbar';
import {
  mapSubRowsKeys,
  SelectedRowData,
} from '../MetricsTable/MetricsTableComponents/SelectedRows/SelectedRows';
import { DrInfoHeader } from './MetricsComponents/DrInfoHeader';
import { TaskFilterOptions } from '../MetricsTable/MetricsTable.utils';
import { TasksEmptyState } from './MetricsComponents/TasksEmptyState';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';

export const Metrics = ({
  isGeneratingAnswers = false,
  withAssociation = false,
  noFilter = true,
  customTaskFilter,
  customSelectedAdditionalTypes,
  customGroupOrOrder,
  setCustomTaskFilter,
  customSelectedMemberId,
  setAdditionalTypesOptions,
}: {
  isGeneratingAnswers?: boolean;
  withAssociation?: boolean;
  withBorder?: boolean;
  noFilter?: boolean;
  customTaskFilter?: TaskFilterOptions;
  customSelectedAdditionalTypes?: string[];
  setCustomTaskFilter?: (val: TaskFilterOptions) => void;
  customGroupOrOrder?: GroupOrderOptions;
  customSelectedMemberId?: string;
  setAdditionalTypesOptions?: (
    s: {
      label: string;
      ref: string;
    }[]
  ) => void;
}) => {
  //added hook to prevent page reload
  const { t } = useTranslation('question');

  const client = useApolloClient();
  const navigate = useNavigate();
  const {
    esrsAssessmentId = '',
    standardRef = '',
    disclosureRequirementRef,
    drType,
    reportingUnitId,
    framework = '',
  } = useParams();
  const { isOpen: isOptOutOpen, onOpen: onOptOutOpen, onClose: onOptOutClose } = useDisclosure();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();
  const [selectedMetric, setSelectedMetric] = useState<SelectedMetric>();
  const [selectedQuarter, setSelectedQuarter] = useState<TimePeriods>(TimePeriodsEnums.year);
  const [selectedRow, setSelectedRow] = useState('');
  const [isTableHovered, setIsTableHovered] = useState('');
  const [groupOrOrder, setGroupOrOrder] = useState(GroupOrderOptions.byType);
  const [taskFilterOption, setTaskFilter] = useState(TaskFilterOptions.allTasks);
  const [selectedAdditionalTypes, setSelectedAdditionalTypes] = useState<string[]>([]);
  const [selectedMaterialityOptions, setSelectedMaterialityOptions] = useState<string[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedExcelMetric, setSelectedExcelMetric] = useState<MetricsTableData>();
  const [isMetricSidebarOpen, setMetricSidebarOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [selectedRows, setSelectedRows] = useState<SelectedRowData[]>([]);
  const [rowData, setRowData] = useState<
    MetricsTableData & { sourceData?: AggregatedQualitativeAnswers[number] }
  >();
  const {
    isOpen: isApprovalWarningModalOpen,
    onOpen: onApprovalWarningModalOpen,
    onClose: onApprovalWarningModalClose,
  } = useDisclosure();

  const [generatedSummaryStatus, setGeneratedSummaryStatus] = useState<GeneratedSummaryStatus>();
  const { colorMode } = useColorMode();
  const { company } = useCurrentCompany();
  const { user } = useAuth();
  const [optOut] = useOptOutMetricMutation();
  const [searchParams] = useSearchParams();
  const urlMetricRef = useMemo(() => searchParams.get('metricRef'), [searchParams]);
  const urlTaskUserId = useMemo(() => searchParams.get('taskUserId'), [searchParams]);
  const urlTaskFilter = useMemo(() => searchParams.get('taskFilter'), [searchParams]);
  const isBusinessUnit = useMemo(() => !!reportingUnitId, [reportingUnitId]);
  const toast = useToast();

  const { data: ruData } = useGetReportingUnitQuery({
    variables: { reportingUnitId },
    skip: !isBusinessUnit,
  });

  const { companyAssessmentId, parentAssessmentId, parentEsrsAssessmentId } = useMaterialStandardId(
    standardRef,
    esrsAssessmentId
  );

  const { companyType } = useCompanyType();
  const isGroup = useMemo(() => companyType === 'group-company', [companyType]);

  const { data: currencyConversionData } = useGetAssessmentCurrencyConversionQuery({
    fetchPolicy: 'network-only',
    variables: {
      assessmentId: esrsAssessmentId,
    },
    skip: !isGroup,
  });

  const {
    allMetrics,
    quantitativeMetrics,
    narrativeMetrics,
    summaryQualitativeMetrics,
    companyLevelReportingUnitId,
    materialityFilterOptions,
    additionalInfoFilterOptions,
    projectLeader,
    requiredMaterialMetrics,
    optedOutMetrics,
    aggregatedMetricsAnswers,
    someTagsNotConfigured,
    taskData,
    tasksLoading,
    notAggregatableMetrics,
    parentNotAggregatableMetrics,
    metricsMissingTagsConfiguration,
    narrativeMetricsMissingTagsConfiguration,
    drData,
    dataLoading,
  } = useProcessMetrics(
    withAssociation,
    customGroupOrOrder ?? groupOrOrder,
    customTaskFilter ?? taskFilterOption,
    customSelectedAdditionalTypes ?? selectedAdditionalTypes,
    customSelectedMemberId ?? selectedMemberId,
    selectedQuarter,
    esrsAssessmentId,
    companyAssessmentId,
    parentAssessmentId,
    isGroup,
    noFilter,
    framework,
    selectedMaterialityOptions
  );

  useEffect(() => {
    if (additionalInfoFilterOptions.length)
      setAdditionalTypesOptions?.(additionalInfoFilterOptions);
  }, []);

  const startReporting = () => {
    optOut({
      variables: {
        objects: selectedRows.map((row) => ({
          metricRef: row.metricRef,
          reportingUnitId: reportingUnitId ?? companyLevelReportingUnitId,
          assessmentId: esrsAssessmentId,
          hasOptedOut: false,
          optOutReason: '',
        })),
      },
      refetchQueries: [
        GetMetricAnswersDocument_,
        GetOptedOutMetricsDocument_,
        GetSingleEsrsMetricAnswerDocument_,
      ],
    }).catch(() => {
      toast({ text: 'Failed to start reporting', variant: 'danger' });
    });
  };

  const filteredNarrativeMetrics = useMemo(() => {
    if (isGroup) {
      return narrativeMetrics;
    }
    return narrativeMetrics
      .map((narrativeMetric) => {
        const isMetricMaterialForCompany =
          narrativeMetric.metric.materialMetrics?.[0]?.isMaterial &&
          narrativeMetric.metric.materialMetrics?.[0]?.materialStandard?.isMaterial;
        const filteredMetric = filterCollectedChildMetrics(
          narrativeMetric,
          parentAssessmentId,
          isGroup,
          requiredMaterialMetrics ?? [],
          isMetricMaterialForCompany,
          isBusinessUnit
        );
        return filteredMetric;
      })
      .filter((metric): metric is AggregatedMetricsTableData => metric !== null);
  }, [narrativeMetrics, parentAssessmentId, isGroup, requiredMaterialMetrics, isBusinessUnit]);

  const { title = '', reference = '', description = '' } = drData || {};
  const isMetricDisclosure = useMemo(() => drType === DREnums.metric, [drType]);
  const [numberOfRows, setNumberOfRows] = useState<Record<string, number>>({});
  const [isComments, setIsComments] = useState<boolean>(false);
  const handleTaskUpdate = useHandleTaskUpdate(
    selectedRows,
    projectLeader?.id,
    companyLevelReportingUnitId
  );

  const isActionOrPolicyOrTarget = useMemo(
    () => drType === DREnums.action || drType === DREnums.policy || drType === DREnums.target,
    [drType]
  );

  useEffect(() => {
    setRowData(undefined);
  }, [disclosureRequirementRef]);

  useEffect(() => {
    if (rowData) setMetricSidebarOpen(true);
    else setMetricSidebarOpen(false);
  }, [rowData]);

  // Quarter selection
  const areMetricsYearly = useMemo(
    () => quantitativeMetrics.every(({ metric }) => !hasQuarterlyFrequency(metric)),
    [quantitativeMetrics]
  );

  const areMetricsQuarterly = useMemo(
    () => quantitativeMetrics.every(({ metric }) => isFullMetricQuarterlyLevel(metric)),
    [quantitativeMetrics]
  );

  const isOnlyNarrative = useMemo(
    () => allMetrics.every((met) => met.metric.metricType !== QuestionType_Enum_.Decimal_),
    [allMetrics]
  );

  useEffect(() => {
    client.refetchQueries({
      include: [
        GetAnswersForMetricsOnCompanyLevelDocument_,
        ...(isGroup
          ? [
              GetAnswersForDrMetricsOnGroupLevelDocument_,
              GetSubAnswersPerDrOnGroupLevelDocument_,
              GetSubMaterialMetricsPerDrOnGroupLevelDocument_,
              GetAnswersForDrMetricsOnCompanyLevelDocument_,
            ]
          : []),
      ],
    });
  }, [isGroup]);

  const [quarterInitialized, setQuarterInitialized] = useState(false);

  useEffect(() => setSelectedRows([]), [selectedQuarter]);

  // Modified effect for setting quarter only runs when data is actually loaded and ready (used to fix the quarter change bug when AI modal is open)
  useEffect(() => {
    if (!dataLoading && !quarterInitialized) {
      if (areMetricsYearly) {
        setSelectedQuarter(TimePeriodsEnums.year);
      } else if (areMetricsQuarterly) {
        setSelectedQuarter(TimePeriodsEnums.q1);
      }
      setQuarterInitialized(true);
    }
  }, [areMetricsYearly, areMetricsQuarterly, quarterInitialized, dataLoading]);

  const [isSelectedMetricYearly, isSelectedMetricQuarterly] = useMemo(() => {
    return [
      selectedExcelMetric ? !hasQuarterlyFrequency(selectedExcelMetric?.metric) : false,
      selectedExcelMetric ? isFullMetricQuarterlyLevel(selectedExcelMetric?.metric) : false,
    ];
  }, [selectedExcelMetric]);

  const showNarrativeSection =
    groupOrOrder === GroupOrderOptions.byType &&
    selectedQuarter === TimePeriodsEnums.year &&
    (!!narrativeMetrics.length || !!summaryQualitativeMetrics.length);

  useEffect(() => {
    if (urlTaskFilter === 'unassigned') {
      setTaskFilter(TaskFilterOptions.unassigned);
    }
    if (urlTaskUserId === user?.id) setTaskFilter(TaskFilterOptions.yourTasks);
    else if (urlTaskUserId) {
      setTaskFilter(TaskFilterOptions.members);
      setSelectedMemberId(urlTaskUserId);
    }
  }, [urlTaskUserId, urlTaskFilter]);

  //Scroll
  useEffect(() => {
    setTimeout(() => {
      const element = document.getElementById(urlMetricRef ?? '');
      if (element && urlMetricRef === element.id && !hasScrolled) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }, 300);
        setHasScrolled(true);
      }
    }, 500);
  }, [urlMetricRef, quantitativeMetrics, narrativeMetrics, hasScrolled]);

  const toggleShowMore = (category: string) => {
    setNumberOfRows((prevStates) => ({
      ...prevStates,
      [category]: (prevStates[category] || 5) + 10,
    }));
  };

  const requiredDrMetrics = useMemo(
    () => requiredMaterialMetrics?.map((m) => m.metric.reference),
    [requiredMaterialMetrics]
  );

  // Reset summary status when rowData changes
  useEffect(() => {
    if (!!generatedSummaryStatus && rowData?.metric.reference !== generatedSummaryStatus.metricRef)
      setGeneratedSummaryStatus(undefined);
  }, [rowData, generatedSummaryStatus]);

  const hasNonOptedOutMetrics = useMemo(
    () => selectedRows.some((row) => !optedOutMetrics?.includes(row.metricRef)),
    [optedOutMetrics, selectedRows]
  );

  const allRowsKeys = useMemo(
    () =>
      uniqBy(
        [
          ...allMetrics.flatMap((m) => mapSubRowsKeys(m, selectedQuarter)),
          ...narrativeMetrics.flatMap((m) => mapSubRowsKeys(m, selectedQuarter)),
        ],
        'rowKey'
      ),
    [allMetrics, narrativeMetrics]
  );

  const selectOptions = [
    {
      id: 'all',
      title: 'All data points',
      onClick: () => setSelectedRows(allRowsKeys),
    },
    {
      id: 'quantitative',
      title: 'All quantitative',
      onClick: () =>
        setSelectedRows(allRowsKeys.filter((m) => m.metricType === QuestionType_Enum_.Decimal_)),
    },
    {
      id: 'narrative',
      title: 'All narrative',
      onClick: () =>
        setSelectedRows(allRowsKeys.filter((m) => m.metricType !== QuestionType_Enum_.Decimal_)),
    },
  ].filter((opt) => (selectedQuarter !== 'Year' ? opt.id === 'quantitative' : true));

  if (dataLoading || (urlTaskUserId && tasksLoading)) return <Loader />;

  return (
    <HStack w="100%" alignItems="start" spacing="16px">
      <VStack
        spacing={isMetricDisclosure ? '8px' : '12px'}
        alignItems="start"
        w="100%"
        padding={'8px'}
      >
        {!isActionOrPolicyOrTarget && (
          <MetricsHeader
            areMetricsYearly={areMetricsYearly}
            drDescription={description ?? ''}
            drRef={reference}
            drTitle={title}
            taskFilterOption={taskFilterOption}
            setTaskFilter={setTaskFilter}
            groupOrOrder={groupOrOrder}
            onDrawerOpen={onDrawerOpen}
            selectedQuarter={selectedQuarter}
            setSelectedMetric={setSelectedMetric}
            setSelectedUserId={setSelectedMemberId}
            selectedUserId={selectedMemberId}
            setGroupOrOrder={setGroupOrOrder}
            setSelectedQuarter={setSelectedQuarter}
            additionalTypesOptions={additionalInfoFilterOptions}
            selectedAdditionalTypes={selectedAdditionalTypes}
            setSelectedAdditionalTypes={setSelectedAdditionalTypes}
            selectedMaterialityOptions={selectedMaterialityOptions}
            setSelectedMaterialityOptions={setSelectedMaterialityOptions}
            materialityFilterOptions={materialityFilterOptions}
          />
        )}

        <VStack
          spacing="40px"
          alignItems="stretch"
          w="100%"
          minH="fit-content"
          mb={isActionOrPolicyOrTarget ? '' : '40px'}
        >
          {(!areMetricsYearly || !(drType === DREnums.action || drType == DREnums.target)) && (
            <VStack alignItems="stretch" gap="12px" w="100%">
              {!areMetricsYearly && <MetricsQuarterInfo selectedQuarter={selectedQuarter} />}
              {!(drType === DREnums.action || drType == DREnums.target) && (
                <MetricsAISection
                  reportingUnitId={reportingUnitId ?? companyLevelReportingUnitId}
                  materialStandardId={companyAssessmentId}
                  disclosureRequirementRef={disclosureRequirementRef ?? ''}
                  allFilteredMetrics={allMetrics}
                  isGroup={isGroup}
                  rowData={rowData}
                  setRowData={setRowData}
                  onOpen={onOptOutOpen}
                  selectedQuarter={selectedQuarter}
                  selectedMetric={selectedMetric}
                  setSelectedMetric={setSelectedMetric}
                  aggregatedMetricsAnswers={aggregatedMetricsAnswers}
                  qualitativeMetrics={narrativeMetrics}
                  summaryQualitativeMetrics={summaryQualitativeMetrics}
                  isPolicy={drType === DREnums.policy}
                  parentStandardId={parentAssessmentId}
                  requiredMaterialMetrics={requiredMaterialMetrics ?? []}
                  currencyConversionData={currencyConversionData?.esrs_AssessmentCurrencyConversion}
                  areMetricsYearly={areMetricsYearly}
                />
              )}
              {((someTagsNotConfigured && notAggregatableMetrics?.length === 0) ||
                notAggregatableMetrics?.length > 0) && (
                <MetricConfigurationWarning
                  navigate={navigate}
                  companyId={company?.id}
                  drRef={disclosureRequirementRef}
                  standardRef={standardRef}
                  esrsAssessmentId={esrsAssessmentId}
                  someTagsNotConfigured={someTagsNotConfigured}
                  hasNonAggregatableMetrics={notAggregatableMetrics.length > 0}
                  framework={framework}
                />
              )}
            </VStack>
          )}
          {!isActionOrPolicyOrTarget && (
            <DrInfoHeader
              drReference={disclosureRequirementRef ?? ''}
              drType={drData.type ?? ''}
              materialStandardId={companyAssessmentId}
              framework={framework}
              isBusinessUnit={isBusinessUnit}
              isGroup={isGroup}
              standardRef={standardRef}
              esrsAssessmentId={esrsAssessmentId}
              parentAssessmentId={parentAssessmentId}
              projectLeader={projectLeader}
              reportingUnitId={reportingUnitId}
              companyReportingUnitId={companyLevelReportingUnitId}
              requiredDrMetrics={requiredDrMetrics}
              parentEsrsAssessmentId={parentEsrsAssessmentId}
            />
          )}

          {!allMetrics.length &&
            !narrativeMetrics.length &&
            (taskFilterOption !== TaskFilterOptions.allTasks ||
              (customTaskFilter && customTaskFilter !== TaskFilterOptions.allTasks) ||
              !!selectedAdditionalTypes.length) && (
              <TasksEmptyState
                selectedUserId={
                  taskFilterOption === TaskFilterOptions.members ? selectedMemberId : undefined
                }
                customTitle={
                  !!selectedAdditionalTypes.length ||
                  taskFilterOption === TaskFilterOptions.markedAsIrrelevant
                    ? 'No matching metrics.'
                    : isActionOrPolicyOrTarget
                      ? 'You currently have no tasks assigned in this section.'
                      : undefined
                }
                customBody={
                  !!selectedAdditionalTypes.length ||
                  taskFilterOption === TaskFilterOptions.markedAsIrrelevant
                    ? 'Reset filters to view the content on this page.'
                    : undefined
                }
                customButtonText={
                  !!selectedAdditionalTypes.length ||
                  taskFilterOption === TaskFilterOptions.markedAsIrrelevant
                    ? 'Reset filters'
                    : undefined
                }
                isSmall={isActionOrPolicyOrTarget}
                resetFilter={() => {
                  setTaskFilter(TaskFilterOptions.allTasks);
                  setCustomTaskFilter?.(TaskFilterOptions.allTasks);
                  setSelectedAdditionalTypes([]);
                }}
              />
            )}

          {!(groupOrOrder === GroupOrderOptions.byType && isOnlyNarrative) && (
            <VStack alignItems="start" gap="12px" w="100%">
              {groupOrOrder === GroupOrderOptions.byType && (
                <Typography variant="h3">Metrics</Typography>
              )}
              <VStack alignItems="start" gap="16px" w="100%">
                {allMetrics?.map((metric, key) => {
                  const isNarrative = metric.metric.metricType !== QuestionType_Enum_.Decimal_;
                  const remainingCount = getRemainingRows(numberOfRows, metric);
                  const taskReferences = getTaskReferences(
                    metric,
                    selectedQuarter,
                    esrsAssessmentId,
                    reportingUnitId ?? companyLevelReportingUnitId
                  );

                  const drTasks = taskData?.Task.filter((task) =>
                    taskReferences.includes(task.taskReference ?? '')
                  );

                  const metricStatus = !!drTasks?.length
                    ? getMetricStatus(drTasks ?? [])
                    : undefined;
                  return (
                    <Skeleton isLoaded={isMetricDisclosure ? true : !dataLoading} width="100%">
                      <VStack
                        alignItems="start"
                        id={metric.metric.reference}
                        spacing="6px"
                        w="100%"
                        boxShadow={
                          colorMode === 'dark'
                            ? '0px 0px 12px 0px #FFFFFF0A, 0px 0px 0px 1px #FFFFFF14 inset'
                            : '0px 0px 12px 0px #0F0F2E0A, 0px 0px 0px 1px #00000014 inset'
                        }
                        padding={'20px'}
                        borderRadius={'12px'}
                        onMouseEnter={() => setIsTableHovered(`${metric.metric.reference}${key}`)}
                        onMouseLeave={() => setIsTableHovered('')}
                      >
                        <MetricTableHeader
                          companyAssessmentId={companyAssessmentId}
                          companyLevelReportingUnitId={companyLevelReportingUnitId}
                          metric={metric}
                          companyName={
                            isBusinessUnit ? (ruData?.bu?.name ?? '') : (company?.name ?? '')
                          }
                          metricStatus={metricStatus}
                          tasksLoading={tasksLoading}
                          drRef={disclosureRequirementRef ?? ''}
                          onImportOpen={onImportOpen}
                          setSelectedExcelMetric={setSelectedExcelMetric}
                        />
                        <MetricsTable
                          key={metric.metric.reference}
                          metrics={[metric]}
                          selectedQuarter={selectedQuarter}
                          isGroup={isGroup}
                          currency={company?.currency ?? ''}
                          isNarrative={isNarrative}
                          checkboxesDisabled={false}
                          materialStandardId={companyAssessmentId}
                          companyReportingUnit={companyLevelReportingUnitId}
                          rowData={rowData}
                          selectedRows={selectedRows}
                          setIsComments={setIsComments}
                          answersData={aggregatedMetricsAnswers}
                          setRowData={setRowData}
                          setSelectedRows={setSelectedRows}
                          pageSize={numberOfRows[metric.metric.reference]}
                          allMetrics={allMetrics}
                          isBusinessUnit={isBusinessUnit}
                          tasksLoading={tasksLoading}
                          setNumberOfRows={setNumberOfRows}
                          isMetricDr={isMetricDisclosure}
                          metricStatus={metricStatus}
                          isGeneratingAnswers={isGeneratingAnswers}
                          onApprovalWarningModalOpen={onApprovalWarningModalOpen}
                          parentStandardId={parentAssessmentId}
                          isTableHovered={isTableHovered === `${metric.metric.reference}${key}`}
                          requiredMaterialMetrics={
                            requiredMaterialMetrics as GetRequiredMaterialMetricsQuery_['esrs_MaterialMetric']
                          }
                          metricsMissingTagsConfiguration={metricsMissingTagsConfiguration}
                          notAggregatableMetrics={notAggregatableMetrics}
                          parentNotAggregatableMetrics={parentNotAggregatableMetrics}
                          optedOutMetrics={optedOutMetrics}
                          currencyConversionData={
                            currencyConversionData?.esrs_AssessmentCurrencyConversion
                          }
                        />
                        {remainingCount > 0 && !isNarrative && (
                          <Button
                            mt="2px"
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleShowMore(metric.metric.reference)}
                          >
                            <HStack spacing="8px">
                              <IconArrowNarrowDown size="16px" />
                              <Typography variant="bodyStrong" size="sm">
                                Load {remainingCount} more
                              </Typography>
                            </HStack>
                          </Button>
                        )}
                        {framework === FrameworkRefs.esrs &&
                          metric.metric.requirement.frameworkRef === FrameworkRefs.esrs && (
                            <Box w="100%" mt={remainingCount > 0 ? '-34px' : '0px'}>
                              <MDRM
                                metric={metric}
                                companyReportingUnitId={companyLevelReportingUnitId}
                                selectedQuarter={selectedQuarter}
                                setRowData={setRowData}
                                setSelectedRows={setSelectedRows}
                                selectedRows={selectedRows}
                                isTableHovered={
                                  isTableHovered === `${metric.metric.reference}${key}`
                                }
                              />
                            </Box>
                          )}
                      </VStack>
                    </Skeleton>
                  );
                })}
              </VStack>
            </VStack>
          )}
          {showNarrativeSection && (
            <VStack alignItems="stretch" gap="12px" w="100%">
              <Typography variant="h3">Narrative data points</Typography>
              <VStack alignItems="start" gap="16px" w="100%">
                {filteredNarrativeMetrics.map((metric, key) => {
                  const taskReferences = getTaskReferences(
                    metric,
                    selectedQuarter,
                    esrsAssessmentId,
                    reportingUnitId ?? companyLevelReportingUnitId
                  );

                  const drTasks = taskData?.Task.filter((task) =>
                    taskReferences.includes(task.taskReference ?? '')
                  );

                  const metricStatus = !!drTasks?.length
                    ? getMetricStatus(drTasks ?? [])
                    : undefined;
                  return (
                    <Skeleton isLoaded={isMetricDisclosure ? true : !dataLoading} w="100%">
                      <VStack
                        alignItems="start"
                        spacing="6px"
                        w="100%"
                        boxShadow={
                          colorMode === 'dark'
                            ? '0px 0px 12px 0px #FFFFFF0A, 0px 0px 0px 1px #FFFFFF14 inset'
                            : '0px 0px 12px 0px #0F0F2E0A, 0px 0px 0px 1px #00000014 inset'
                        }
                        padding={'12px 20px 20px 20px'}
                        borderRadius={'12px'}
                        onMouseEnter={() => setIsTableHovered(`${metric.metric.reference}${key}`)}
                        onMouseLeave={() => setIsTableHovered('')}
                      >
                        <MetricsTable
                          key={metric.metric.reference}
                          metrics={[metric]}
                          isNarrative
                          selectedQuarter={selectedQuarter}
                          isGroup={isGroup}
                          metricStatus={metricStatus}
                          currency={company?.currency ?? ''}
                          selectedRows={selectedRows}
                          setSelectedRow={setSelectedRow}
                          materialStandardId={companyAssessmentId}
                          companyReportingUnit={companyLevelReportingUnitId}
                          isTableHovered={isTableHovered === `${metric.metric.reference}${key}`}
                          rowData={rowData}
                          isGeneratingAnswers={isGeneratingAnswers}
                          setIsComments={setIsComments}
                          onApprovalWarningModalOpen={onApprovalWarningModalOpen}
                          answersData={aggregatedMetricsAnswers}
                          setRowData={setRowData}
                          allMetrics={allMetrics}
                          setSelectedRows={setSelectedRows}
                          setNumberOfRows={setNumberOfRows}
                          checkboxesDisabled={false}
                          tasksLoading={tasksLoading}
                          isMetricDr={isMetricDisclosure}
                          parentStandardId={parentAssessmentId}
                          requiredMaterialMetrics={requiredMaterialMetrics ?? []}
                          metricsMissingTagsConfiguration={narrativeMetricsMissingTagsConfiguration}
                          notAggregatableMetrics={notAggregatableMetrics}
                          parentNotAggregatableMetrics={parentNotAggregatableMetrics}
                          optedOutMetrics={optedOutMetrics}
                          currencyConversionData={
                            currencyConversionData?.esrs_AssessmentCurrencyConversion
                          }
                        />
                      </VStack>
                    </Skeleton>
                  );
                })}
              </VStack>
            </VStack>
          )}
        </VStack>

        <LearnMoreDrawer
          header={selectedMetric?.isDisclosure ? selectedMetric.title : undefined}
          isOpen={isDrawerOpen}
          onClose={onDrawerClose}
          description={selectedMetric?.description ?? ''}
          customHeader={
            !selectedMetric?.isDisclosure ? (
              <MetricLearnMoreHeader
                metricRef={selectedMetric?.reference ?? ''}
                tags={selectedMetric?.tags ?? []}
                frameworkName={
                  Frameworks[selectedMetric?.frameworkRef as keyof typeof FrameworkRefs]
                }
              />
            ) : undefined
          }
        />

        <OptOutModal
          key={rowData?.metric.reference}
          rowData={rowData}
          isOpen={isOptOutOpen}
          onClose={onOptOutClose}
          selectedRows={selectedRows}
          assessmentId={esrsAssessmentId}
          reportingUnitId={companyLevelReportingUnitId}
          metric={selectedMetric}
          taskData={taskData}
          isCompany={false}
          selectedTimeframe={selectedQuarter}
        />
      </VStack>
      {/* Normal metric sidebar */}
      {!!rowData && (
        <MetricSidebar
          row={rowData}
          metric={rowData?.metric ?? ({} as MetricsTableData['metric'])}
          materialStandardId={companyAssessmentId}
          companyReportingUnit={companyLevelReportingUnitId}
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
          onMenuOpen={onOptOutOpen}
          selectedQuarter={selectedQuarter}
          setRowData={setRowData}
          isComments={isComments}
          isGroup={isGroup}
          companyCurrency={company?.currency ?? ''}
          generatedSummaryStatus={generatedSummaryStatus}
          setGeneratedSummaryStatus={setGeneratedSummaryStatus}
          frameworkName={
            Frameworks[rowData?.metric?.requirement?.frameworkRef as keyof typeof FrameworkRefs]
          }
        />
      )}
      {/* MDRM sidebar based on query param */}
      {!rowData && !!searchParams.get('openMdrm') && (
        <Box w="320px" minWidth="320px" overflowY="auto" />
      )}
      {isMetricSidebarOpen && <Box minW="336px"></Box>}

      <Suspense>
        {selectedExcelMetric && (
          <MetricsExcelUploadModal
            isOpen={isImportOpen}
            onClose={onImportClose}
            metrics={[selectedExcelMetric]}
            disclosureRequirement={disclosureRequirementRef ?? ''}
            businessUnit={company?.name ?? 'Company'}
            areTableMetricsQuarterly={isSelectedMetricQuarterly}
            areTableMetricsYearly={isSelectedMetricYearly}
            companyStandardId={companyAssessmentId}
            companyReportingUnitId={companyLevelReportingUnitId}
          />
        )}
      </Suspense>
      <AnswersApprovalWarningModal
        isOpen={isApprovalWarningModalOpen}
        onClose={onApprovalWarningModalClose}
        onConfirm={() => {
          const metric = allMetrics
            .filter((met) => met.metric.metricType !== QuestionType_Enum_.Decimal_)
            .find((m) => m.metric.reference === selectedRow)?.metric;

          const answerData = aggregatedMetricsAnswers?.find((a) => a.metricRef === selectedRow);

          if (isEqual(rowData?.metric, metric)) {
            setRowData(undefined);
          } else
            setRowData(
              !!answerData?.reportingUnits?.length || !!answerData?.subsidiaries?.length
                ? { metric: metric as MetricsTableData['metric'], sourceData: answerData }
                : { metric: metric as MetricsTableData['metric'] }
            );
        }}
      />
      {selectedRows.length && (
        <TaskToolbar
          selectOptions={selectOptions}
          selectedText={`${selectedRows.length} data point${selectedRows.length > 1 ? 's' : ''} selected`}
          handleTaskUpdate={handleTaskUpdate}
          onClose={() => setSelectedRows([])}
          onOptOutClick={() => {
            hasNonOptedOutMetrics ? onOptOutOpen() : startReporting();
          }}
          showOptOutOption={hasNonOptedOutMetrics}
        />
      )}
    </HStack>
  );
};
